import dayjs from 'dayjs';
import { Field, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Form, FormGroup, InlineNotification, Stack, TextInput } from '@carbon/react';
import { FormikTextInput, FormikToggle } from 'src/components/Form/FormikInputs';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useExportZaloConfig, useSaveZaloIntegration, useZaloIntegration } from 'src/hooks/useIntegrations';
import { useMemberships } from 'src/hooks/useOrganizations';
import { ZaloIntegrationWriteDto } from 'src/services/apis/types';
import { DEFAULT_BRAND_COLOR, isBrandColor, onPrimary } from 'src/utils/brandColor';

const Schema = z.object({
  appId: z.string().trim().min(1),
  oaId: z.string().trim().optional(),
  brandPrimaryColor: z
    .string()
    .trim()
    .refine((value) => value === '' || isBrandColor(value), { message: 'brandPrimaryColorInvalid' }),
});

type ZaloFormValues = {
  appId: string;
  oaId: string;
  enabled: boolean;
  secretKey: string;
  checkoutPrivateKey: string;
  brandPrimaryColor: string;
};

// The origin the Mini App must be allowed to call — the same one the portal itself calls
// (see src/services/apis/config.ts). Zalo's console needs this whitelisted; there is no
// DTO field or endpoint for it because the portal already knows it client-side.
const PERMITTED_DOMAIN = import.meta.env.VITE_API_URL ?? '';

// The name apps/zalo-mini/src/restavo-config.json expects. Saving it under any other name
// means the organization has to rename it before `zmp build`, and a config the Mini App
// cannot find degrades to the platform defaults with no error.
const CONFIG_FILENAME = 'restavo-config.json';

const IntegrationsZalo = React.memo(() => {
  const { t } = useTranslation();
  const { isOwner } = useAuthPermissions();
  const { data: zalo } = useZaloIntegration();
  const saveZalo = useSaveZaloIntegration();
  const exportZalo = useExportZaloConfig();
  const { activeOrganizationId } = useAuthSession();
  const { data: memberships } = useMemberships();
  const activeOrganization = memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0];
  const [saved, setSaved] = useState(false);
  const [domainCopied, setDomainCopied] = useState(false);

  async function copyPermittedDomain() {
    await navigator.clipboard.writeText(PERMITTED_DOMAIN);
    setDomainCopied(true);
  }

  /**
   * Branding only reaches a diner through this file: the organization builds and deploys the
   * Mini App itself (design §2.1), so a colour saved here is inert until it is downloaded.
   */
  function downloadConfig() {
    exportZalo.mutate(undefined, {
      onSuccess: (config) => {
        const url = URL.createObjectURL(new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = CONFIG_FILENAME;
        link.click();
        URL.revokeObjectURL(url);
      },
    });
  }

  const initialValues: ZaloFormValues = {
    appId: zalo?.appId ?? '',
    oaId: zalo?.oaId ?? '',
    enabled: zalo?.enabled ?? false,
    // Secret inputs always start empty, even when a secret is already stored — the mask
    // is display-only. Pre-filling with the mask would post it back as the new secret.
    secretKey: '',
    checkoutPrivateKey: '',
    brandPrimaryColor: zalo?.brandPrimaryColor ?? '',
  };

  function handleSubmit(values: ZaloFormValues, helpers: { setFieldValue: (field: string, value: unknown) => void }) {
    setSaved(false);
    const payload: ZaloIntegrationWriteDto = {
      appId: values.appId,
      enabled: values.enabled,
      oaId: values.oaId || undefined,
      // An untouched (empty) secret field is omitted entirely — the server treats an
      // omitted field as "leave the stored value unchanged".
      ...(values.secretKey ? { secretKey: values.secretKey } : {}),
      ...(values.checkoutPrivateKey ? { checkoutPrivateKey: values.checkoutPrivateKey } : {}),
      // An empty field is omitted, matching how the secret fields are handled above.
      // Trimmed here as well as in the schema: zod validates a trimmed copy but Formik keeps
      // the raw field value, so ' #A0673D ' passed the form and then 422'd at the server.
      ...(values.brandPrimaryColor.trim() ? { brandPrimaryColor: values.brandPrimaryColor.trim() } : {}),
    };
    saveZalo.mutate(payload, {
      onSuccess: () => {
        setSaved(true);
        helpers.setFieldValue('secretKey', '');
        helpers.setFieldValue('checkoutPrivateKey', '');
      },
    });
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={toFormikValidationSchema(Schema)}
      onSubmit={handleSubmit}
    >
      {/* Carbon's <Form> is a bare <form> that spreads its props — it knows nothing about
          Formik, so without this onSubmit the Save button submitted nothing at all and the
          page could not store a single setting. Every other Formik form in this app wires it
          the same way (see Onboarding/*, SignIn.tsx). */}
      {({ handleSubmit: submitForm }) => (
        <Form onSubmit={submitForm}>
          <FormGroup legendText={t('integrations.zalo.name')}>
            <Stack gap={5}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                <TextInput
                  id="zalo-permitted-domain"
                  labelText={t('integrations.zalo.permittedDomain')}
                  helperText={t('integrations.zalo.permittedDomainHint')}
                  value={PERMITTED_DOMAIN}
                  readOnly
                />
                <Button kind="tertiary" size="sm" onClick={copyPermittedDomain}>
                  {t('integrations.zalo.copyDomain')}
                </Button>
              </div>
              {domainCopied && (
                <InlineNotification
                  kind="success"
                  title={t('integrations.zalo.domainCopied')}
                  lowContrast
                  onClose={() => setDomainCopied(false)}
                />
              )}
              {!isOwner && (
                <InlineNotification kind="info" title={t('integrations.zalo.ownerOnly')} lowContrast hideCloseButton />
              )}
              {saved && (
                <InlineNotification
                  kind="success"
                  title={t('integrations.zalo.saved')}
                  lowContrast
                  onClose={() => setSaved(false)}
                />
              )}
              {saveZalo.isError && (
                <InlineNotification
                  kind="error"
                  title={(saveZalo.error as Error).message}
                  lowContrast
                  hideCloseButton
                />
              )}
              {/* An org with no saved integration row gets a 404 here. Silence would look
                  like a button that does nothing. */}
              {exportZalo.isError && (
                <InlineNotification
                  kind="error"
                  title={t('integrations.zalo.downloadConfigFailed')}
                  subtitle={(exportZalo.error as Error).message}
                  lowContrast
                  onClose={() => exportZalo.reset()}
                />
              )}
              <FormikTextInput name="appId" label={t('integrations.zalo.appId')} />
              <FormikTextInput name="oaId" label={t('integrations.zalo.oaId')} />
              <FormikToggle name="enabled" label={t('integrations.zalo.enabled')} />
              <Field name="brandPrimaryColor">
                {({ field, meta }) => (
                  <TextInput
                    id="zalo-brand-primary-color"
                    labelText={t('integrations.zalo.brandPrimaryColor')}
                    placeholder={DEFAULT_BRAND_COLOR}
                    disabled={!isOwner}
                    invalid={!!(meta.touched && meta.error)}
                    // `meta.error` is the zod refine's raw message key (`brandPrimaryColorInvalid`),
                    // not localized text — zod-i18n-map is not wired into this app (see
                    // Onboarding/schemas.ts), so it has to be translated here explicitly.
                    invalidText={meta.error ? t(`integrations.zalo.${meta.error}`) : undefined}
                    {...field}
                  />
                )}
              </Field>
              <Field name="brandPrimaryColor">
                {({ field }) => {
                  // One value drives both halves. Deriving the text colour from the raw field
                  // instead would pair the fallback background with onPrimary('')'s white,
                  // which is readable only because today's default happens to be dark.
                  const previewColor = isBrandColor(field.value?.trim() ?? '')
                    ? field.value.trim()
                    : DEFAULT_BRAND_COLOR;
                  return (
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      {/* The preview renders the derived text colour, so the owner sees the
                          contrast rule act BEFORE saving rather than after a diner complains. */}
                      <span
                        style={{
                          background: previewColor,
                          color: onPrimary(previewColor),
                          padding: '0.5rem 1rem',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                        }}
                      >
                        {t('integrations.zalo.brandPreview')}
                      </span>
                      <span style={{ fontSize: '0.75rem' }}>{t('integrations.zalo.brandPrimaryColorHint')}</span>
                    </div>
                  );
                }}
              </Field>
              <TextInput
                id="zalo-brand-name"
                labelText={t('integrations.zalo.brandName')}
                value={activeOrganization?.name ?? ''}
                readOnly
                helperText={t('integrations.zalo.brandInherited')}
              />
              <TextInput
                id="zalo-brand-logo"
                labelText={t('integrations.zalo.brandLogo')}
                value={activeOrganization?.logo ?? ''}
                readOnly
              />
              {/* Relative, not absolute: `org-settings` and `integrations` are siblings in
                  orgLeafRoutes (App.tsx), so going up two levels from integrations/zalo lands
                  on the shared parent whatever tenant prefix is mounted above it. */}
              <Link to="../../org-settings">{t('integrations.zalo.brandInheritedLink')}</Link>
              <Field name="secretKey">
                {({ field, meta }) => (
                  <TextInput.PasswordInput
                    id="zalo-secret-key"
                    labelText={t('integrations.zalo.secretKey')}
                    helperText={
                      zalo?.hasSecretKey
                        ? t('integrations.zalo.secretKeySet', { mask: zalo.secretKeyMask })
                        : t('integrations.zalo.secretKeyNotSet')
                    }
                    placeholder={t('integrations.zalo.secretKeyPlaceholder')}
                    disabled={!isOwner}
                    invalid={!!(meta.touched && meta.error)}
                    invalidText={meta.error}
                    {...field}
                  />
                )}
              </Field>
              <Field name="checkoutPrivateKey">
                {({ field, meta }) => (
                  <TextInput.PasswordInput
                    id="zalo-checkout-private-key"
                    labelText={t('integrations.zalo.checkoutPrivateKey')}
                    helperText={
                      zalo?.hasCheckoutPrivateKey
                        ? t('integrations.zalo.checkoutPrivateKeySet')
                        : t('integrations.zalo.checkoutPrivateKeyNotSet')
                    }
                    placeholder={t('integrations.zalo.checkoutPrivateKeyPlaceholder')}
                    disabled={!isOwner}
                    invalid={!!(meta.touched && meta.error)}
                    invalidText={meta.error}
                    {...field}
                  />
                )}
              </Field>
              {zalo?.lastExchangeAt && (
                <TextInput
                  id="zalo-last-exchange"
                  labelText={t('integrations.zalo.lastExchangeAt')}
                  value={dayjs(zalo.lastExchangeAt).format('DD/MM/YYYY HH:mm')}
                  readOnly
                />
              )}
              <Button type="submit" disabled={!isOwner || saveZalo.isPending}>
                {t('actions.save')}
              </Button>
              <div>
                <Button kind="tertiary" onClick={downloadConfig} disabled={!isOwner || exportZalo.isPending}>
                  {t('integrations.zalo.downloadConfig')}
                </Button>
                <p style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>{t('integrations.zalo.downloadConfigHint')}</p>
              </div>
            </Stack>
          </FormGroup>
        </Form>
      )}
    </Formik>
  );
});

export default IntegrationsZalo;
